/**
 * Iter-411m — Venues With Tight Radius (Admin)
 * =============================================
 *
 * Read-only report of upcoming shifts whose GPS geofence radius appears
 * too small for the venue. Admin can accept the recommendation with one
 * click, or override with a custom value. Never blocks shift execution
 * — this is purely a proactive warning surface.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, MapPin, RefreshCw, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const RISK_STYLES = {
  high:    { label: 'High risk',   bg: 'bg-red-100 text-red-800 border-red-200' },
  medium:  { label: 'Medium risk', bg: 'bg-amber-100 text-amber-800 border-amber-200' },
  low:     { label: 'Low risk',    bg: 'bg-yellow-50 text-yellow-800 border-yellow-200' },
  ok:      { label: 'OK',          bg: 'bg-green-100 text-green-800 border-green-200' },
  unknown: { label: 'Unknown',     bg: 'bg-slate-100 text-slate-700 border-slate-200' },
};

export default function VenuesTightRadius() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [daysAhead, setDaysAhead] = useState(30);
  const [includeOk, setIncludeOk] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [customRadius, setCustomRadius] = useState({});

  const token = () => localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/api/admin/radius/tight-shifts?days_ahead=${daysAhead}&include_ok=${includeOk}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setRows(data.shifts || []);
    } catch (e) {
      toast.error('Failed to load tight-radius shifts', { description: String(e).slice(0, 200) });
    } finally {
      setLoading(false);
    }
  }, [daysAhead, includeOk]);

  useEffect(() => { load(); }, [load]);

  const applyRadius = async (shift, radius) => {
    setSavingId(shift.shift_id);
    try {
      const res = await fetch(`${API}/api/admin/radius/shift/${shift.shift_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({ radius_m: radius, reason: 'venue_tight_radius_report' }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success(`Radius updated to ${radius}m`);
      await load();
    } catch (e) {
      toast.error('Failed to update radius', { description: String(e).slice(0, 200) });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="venues-tight-radius-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
            Venues With Tight Radius
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Upcoming shifts whose GPS geofence looks smaller than the venue really requires.
            Fixing these proactively prevents talent from being unable to clock in or out onsite.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="tight-radius-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-slate-50 rounded-lg border">
        <label className="text-sm font-medium">Days ahead:</label>
        <Input
          type="number"
          min={1}
          max={180}
          value={daysAhead}
          onChange={(e) => setDaysAhead(parseInt(e.target.value || '30', 10))}
          className="w-24"
          data-testid="tight-radius-days-input"
        />
        <label className="flex items-center gap-2 text-sm ml-4">
          <input
            type="checkbox"
            checked={includeOk}
            onChange={(e) => setIncludeOk(e.target.checked)}
            data-testid="tight-radius-include-ok-toggle"
          />
          Also show correctly-sized shifts
        </label>
        <span className="text-sm text-slate-500 ml-auto" data-testid="tight-radius-count">
          {rows.length} shift{rows.length === 1 ? '' : 's'}
        </span>
      </div>

      {loading && rows.length === 0 && (
        <div className="text-center py-12 text-slate-500">Loading upcoming shifts…</div>
      )}

      {!loading && rows.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-medium">No shifts need attention</p>
            <p className="text-sm text-slate-600 mt-1">
              Every upcoming shift is within the recommended geofence range.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {rows.map((r) => {
          const risk = RISK_STYLES[r.risk_level] || RISK_STYLES.unknown;
          const custom = customRadius[r.shift_id];
          return (
            <Card key={r.shift_id} data-testid={`tight-radius-row-${r.shift_id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-500" />
                      {r.venue_name || r.venue_address || 'Unnamed venue'}
                    </CardTitle>
                    <p className="text-sm text-slate-600 mt-1">
                      {r.event_title || 'Shift'} · {r.city}, {r.state} · {r.shift_date} {r.start_time && `· ${r.start_time}`}
                    </p>
                  </div>
                  <Badge className={`${risk.bg} border`}>{risk.label}</Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                  <div>
                    <div className="text-slate-500 text-xs">Current radius</div>
                    <div className="font-semibold text-lg">{r.current_m} m</div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs">Recommended</div>
                    <div className="font-semibold text-lg text-blue-700">{r.recommended_m} m</div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs">Assigned talent</div>
                    <div className="font-semibold text-lg">{r.assigned_talent_count}</div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs">Confidence</div>
                    <div className="font-semibold text-lg capitalize">{r.confidence}</div>
                  </div>
                </div>
                <p className="text-sm text-slate-700 bg-slate-50 border rounded p-3 mb-3">{r.reason}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => applyRadius(r, r.recommended_m)}
                    disabled={savingId === r.shift_id}
                    data-testid={`tight-radius-apply-${r.shift_id}`}
                  >
                    Apply {r.recommended_m}m
                  </Button>
                  <Input
                    type="number"
                    min={100}
                    max={2000}
                    placeholder="Custom (m)"
                    value={custom || ''}
                    onChange={(e) => setCustomRadius({ ...customRadius, [r.shift_id]: e.target.value })}
                    className="w-32"
                    data-testid={`tight-radius-custom-input-${r.shift_id}`}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!custom || savingId === r.shift_id}
                    onClick={() => applyRadius(r, parseInt(custom, 10))}
                    data-testid={`tight-radius-apply-custom-${r.shift_id}`}
                  >
                    Apply Custom
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
